import { createRoute } from '@tanstack/react-router'
import { rootRoute } from './__root'
import { ArrowUpRightIcon, PlayIcon, AppleIcon, GlobeIcon } from '../components/Icons'

export const gamesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/games',
  component: GamesComponent,
})

const JOGA_TRUCO_LINKS = {
  web: 'https://jogatruco.com/',
  android: 'https://play.google.com/store/apps/details?id=com.cloudymedia.jogatruco',
  ios: 'https://apps.apple.com/br/app/joga-truco/id6821099759',
}

const GRAN_TRUCO_LINKS = {
  web: 'https://trucogame.com/',
  android:
    'https://play.google.com/store/apps/details?id=com.cloudymedia.trucowebview&hl=es_AR',
  ios: 'https://apps.apple.com/ar/app/gran-truco-argentino/id6753727331',
}

function GamesComponent() {
  return (
    <div className="page games-page">
      <div className="container">
        <header className="page-header">
          <span className="eyebrow">games</span>
          <h1 className="page-title">Our games</h1>
          <p className="page-subtitle">
            Truco, the card game of bluffing and banter, rebuilt for mobile and
            web. One for Argentina, one for Brazil. Real rules, real opponents,
            real-time.
          </p>
        </header>
      </div>

      <section className="section" id="joga-truco" lang="pt-BR">
        <div className="container">
          <div className="feature">
            <div className="feature-content">
              <div className="tag-list">
                <span className="tag accent">lançamento</span>
                <span className="tag">Truco Paulista</span>
                <span className="tag">Truco Mineiro</span>
              </div>
              <h2 className="feature-title">
                Joga Truco: Truco Paulista e Truco Mineiro online
              </h2>
              <p className="feature-text">
                Jogue truco online grátis com seus amigos ou contra jogadores de
                todo o Brasil. O Joga Truco traz as regras do Truco Paulista,
                com vira e manilha, e do Truco Mineiro, com zap, copas,
                espadilha e pica-fumo, em partidas em tempo real.
              </p>
              <p className="feature-text">
                Peça truco, aumente para seis, nove e doze, blefe na mão de onze
                e jogue no celular ou direto no navegador, sem instalar nada.
              </p>
              <div className="feature-actions">
                <a
                  href={JOGA_TRUCO_LINKS.android}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary"
                  title="Baixar Joga Truco no Google Play"
                >
                  <PlayIcon />
                  Google Play
                </a>
                <a
                  href={JOGA_TRUCO_LINKS.ios}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary"
                  title="Baixar Joga Truco na App Store"
                >
                  <AppleIcon />
                  App Store
                </a>
                <a
                  href={JOGA_TRUCO_LINKS.web}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                  title="Jogar truco online no navegador"
                >
                  <GlobeIcon />
                  Jogar truco online
                  <ArrowUpRightIcon />
                </a>
              </div>
            </div>

            <div className="spec-list" aria-label="Especificações do jogo">
              <div className="spec-row">
                <span className="spec-key">title</span>
                <span className="spec-val">Joga Truco</span>
              </div>
              <div className="spec-row">
                <span className="spec-key">variants</span>
                <span className="spec-val">Truco Paulista · Truco Mineiro</span>
              </div>
              <div className="spec-row">
                <span className="spec-key">mode</span>
                <span className="spec-val">Multiplayer online</span>
              </div>
              <div className="spec-row">
                <span className="spec-key">platforms</span>
                <span className="spec-val">iOS, Android, Web</span>
              </div>
              <div className="spec-row">
                <span className="spec-key">market</span>
                <span className="spec-val">Brasil</span>
              </div>
              <div className="spec-row">
                <span className="spec-key">status</span>
                <span className="spec-val accent">lançamento</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="gran-truco-argentino">
        <div className="container">
          <div className="feature">
            <div className="feature-content">
              <div className="tag-list">
                <span className="tag accent">2M+ players</span>
                <span className="tag accent">4.9 rating</span>
                <span className="tag accent">100K+ games / day</span>
              </div>
              <h2 className="feature-title">Gran Truco Argentino</h2>
              <p className="feature-text">
                Truco is more than a card game in Argentina: it is a ritual of
                bluffing, signals and friendly trash talk. Gran Truco Argentino
                brings that experience to your phone and browser, with online
                matches against friends or players from around the world.
              </p>
              <p className="feature-text">
                Play on whatever is closest to you. Progress and matches work
                the same across every platform.
              </p>
              <div className="feature-actions">
                <a
                  href={GRAN_TRUCO_LINKS.android}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary"
                >
                  <PlayIcon />
                  Google Play
                </a>
                <a
                  href={GRAN_TRUCO_LINKS.ios}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary"
                >
                  <AppleIcon />
                  App Store
                </a>
                <a
                  href={GRAN_TRUCO_LINKS.web}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  <GlobeIcon />
                  Play on web
                  <ArrowUpRightIcon />
                </a>
              </div>
            </div>

            <div className="spec-list" aria-label="Game specifications">
              <div className="spec-row">
                <span className="spec-key">title</span>
                <span className="spec-val">Gran Truco Argentino</span>
              </div>
              <div className="spec-row">
                <span className="spec-key">genre</span>
                <span className="spec-val">Card · Strategy</span>
              </div>
              <div className="spec-row">
                <span className="spec-key">mode</span>
                <span className="spec-val">Online multiplayer</span>
              </div>
              <div className="spec-row">
                <span className="spec-key">platforms</span>
                <span className="spec-val">iOS, Android, Web</span>
              </div>
              <div className="spec-row">
                <span className="spec-key">developer</span>
                <span className="spec-val">Cloudymedia</span>
              </div>
              <div className="spec-row">
                <span className="spec-key">status</span>
                <span className="spec-val accent">live</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
